<template>
  <div class="term-group" :style="indentStyle">
    <div
      class="term-header"
      role="button"
      tabindex="0"
      @click="$emit('toggle')"
      @keydown.enter.prevent="$emit('toggle')"
      @keydown.space.prevent="$emit('toggle')"
    >
      <span class="term-main">
        <span class="arrow" :class="{open: state.expanded}">▶</span>
        <label v-if="showVisibility" class="switch is-small" @click.stop>
          <input type="checkbox" :checked="visible" @change="$emit('toggle-visibility')">
          <span class="switch-slider"></span>
        </label>
        <span class="dot" :style="{background: term.color || '#9575CD'}"></span>
        <span class="term-label">{{ term.name }}</span>
      </span>
      <label v-if="showReportSelection" class="report-term-selection" @click.stop>
        <input
          type="checkbox"
          data-testid="report-term-checkbox"
          :checked="reportSelected"
          @change="$emit('toggle-report-selection')"
        >
      </label>
    </div>

    <div v-if="showConfidenceThreshold" class="term-confidence-control">
      <button
        class="term-confidence-toggle"
        type="button"
        data-testid="term-confidence-toggle"
        @click.stop="confidenceExpanded = !confidenceExpanded"
      >
        <span class="term-arrow">{{ confidenceExpanded ? '▼' : '▶' }}</span>
        <span>{{ $t('confidence-threshold') }}</span>
        <strong>{{ draftConfidenceThreshold.toFixed(2) }}</strong>
      </button>
      <input
        v-if="confidenceExpanded"
        class="slider is-fullwidth"
        data-testid="term-confidence-slider"
        type="range"
        min="0"
        max="1"
        step="0.01"
        :value="draftConfidenceThreshold"
        @input="draftConfidenceThreshold = Number($event.target.value)"
        @change="commitConfidenceThreshold($event.target.value)"
      >
    </div>

    <div v-if="state.expanded" class="annotation-list">
      <b-loading :is-full-page="false" class="small" :active="state.loading" />
      <b-message v-if="state.error" type="is-danger" has-icon icon-size="is-small">
        {{ $t('failed-to-load-annotations') }}
        <button class="button is-small" type="button" @click.stop="$emit('retry')">{{ $t('retry') }}</button>
      </b-message>
      <em v-else-if="!state.items.length && !state.loading" class="empty-message">{{ $t('no-annotation') }}</em>
      <div v-else class="annotation-previews">
        <annotation-preview
          v-for="annot in state.items"
          :key="annot.id"
          class="annotation-preview"
          :annot="annot"
          :size="72"
          :color="termColorHex"
          :terms="terms"
          :images="images"
          :show-image-info="false"
          :same-view-on-click="true"
          @updateTermsOrTracks="$emit('updateTermsOrTracks', annot)"
          @centerView="$emit('centerView', {annot, sameView: true})"
          @deletion="$emit('delete', annot)"
          @select="$emit('select', $event)"
        />
      </div>
      <cytomine-pagination
        v-if="paginated"
        class="term-pagination"
        :total="total"
        :current="currentPage"
        :per-page="perPage"
        @change="$emit('page-change', $event)"
      />
      <button
        v-else-if="state.hasMore"
        class="button is-small is-dark load-more"
        type="button"
        :disabled="state.loading"
        @click.stop="$emit('load-more')"
      >
        {{ $t('button-load-more') }}
      </button>
    </div>
  </div>
</template>

<script>
import AnnotationPreview from '@/components/annotations/AnnotationPreview';
import CytominePagination from '@/components/utils/CytominePagination';

export default {
  name: 'AnnotationTermRow',
  components: {AnnotationPreview, CytominePagination},
  props: {
    term: {type: Object, required: true},
    state: {type: Object, required: true},
    visible: {type: Boolean, default: true},
    showVisibility: {type: Boolean, default: true},
    showReportSelection: {type: Boolean, default: false},
    reportSelected: {type: Boolean, default: false},
    showConfidenceThreshold: {type: Boolean, default: false},
    confidenceThreshold: {type: Number, default: 0},
    terms: {type: Array, default: () => []},
    images: {type: Array, default: () => []},
    depth: {type: Number, default: 0},
    paginated: {type: Boolean, default: false},
    currentPage: {type: Number, default: 1},
    total: {type: Number, default: 0},
    perPage: {type: Number, default: 24}
  },
  data() {
    return {
      confidenceExpanded: false,
      draftConfidenceThreshold: this.confidenceThreshold
    };
  },
  watch: {
    confidenceThreshold(value) {
      this.draftConfidenceThreshold = value;
    }
  },
  computed: {
    indentStyle() {
      return this.depth ? {marginLeft: `${this.depth * 1.1}rem`} : null;
    },
    termColorHex() {
      return (this.term.colorHex || this.term.color || '#9575CD').replace(/^#/, '');
    }
  },
  methods: {
    commitConfidenceThreshold(value) {
      const threshold = Math.max(0, Math.min(1, Number(value) || 0));
      this.draftConfidenceThreshold = threshold;
      this.$emit('confidence-threshold-change', threshold);
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../assets/styles/dark-variables';

.term-group {
  overflow: hidden;
  border: 1px solid rgba(190, 214, 238, 0.12);
  border-radius: 8px;
  background: rgba(222, 238, 255, 0.032);
}

.term-header {
  width: 100%;
  min-height: 2.7rem;
  padding: 0.45rem 0.6rem;
  display: flex;
  align-items: center;
  border: 0;
  background: transparent;
  color: $dark-text-primary;
  cursor: pointer;
  text-align: left;
  justify-content: space-between;
}

.report-term-selection {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 1.8rem;
  min-height: 1.8rem;
  margin-left: .5rem;
  cursor: pointer;
}

.report-term-selection input {
  width: 1rem;
  height: 1rem;
  accent-color: $primary;
  cursor: pointer;
}

.term-confidence-control {
  padding: .35rem .65rem .55rem;
  border-top: 1px solid rgba(190, 214, 238, .08);
  background: rgba(8, 17, 29, .14);
}

.term-confidence-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  gap: .4rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: $dark-text-secondary;
  cursor: pointer;
  font-size: .75rem;
  text-align: left;
}

.term-confidence-toggle strong {
  margin-left: auto;
  color: $dark-text-primary;
}

.term-confidence-control .slider {
  margin-top: .45rem;
}

.term-header:hover {
  background: rgba($primary, 0.07);
}

.term-main {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 0;
  font-weight: 600;
}

.term-label {
  overflow: hidden;
  text-overflow: ellipsis;
}

.arrow {
  color: $dark-text-disabled;
  transition: transform 0.18s ease, color 0.18s ease;
}

.arrow.open {
  transform: rotate(90deg);
  color: $primary;
}

.dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  min-width: 12px;
  min-height: 12px;
  border-radius: 50%;
}

.switch {
  position: relative;
  display: inline-block;
  flex: 0 0 auto;
  width: 42px;
  height: 22px;
}

.switch input {
  display: none;
}

.switch-slider {
  position: absolute;
  inset: 0;
  border: 1px solid rgba(190, 214, 238, 0.15);
  border-radius: 20px;
  background: rgba(132, 156, 184, 0.16);
  transition: 0.2s;
}

.switch-slider:before {
  position: absolute;
  bottom: 1px;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: $dark-text-secondary;
  content: '';
  transition: 0.2s;
}

input:checked + .switch-slider {
  border-color: $primary;
  background: $primary;
}

input:checked + .switch-slider:before {
  transform: translateX(19px);
}

.switch.is-small {
  width: 34px;
  height: 18px;
}

.switch.is-small .switch-slider:before {
  width: 14px;
  height: 14px;
}

.switch.is-small input:checked + .switch-slider:before {
  transform: translateX(16px);
}

.annotation-list {
  position: relative;
  min-height: 3rem;
  padding: 0.65rem 0.8rem;
  border-top: 1px solid rgba(190, 214, 238, 0.1);
  background: rgba(8, 17, 29, 0.28);
}

.annotation-previews {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.annotation-preview {
  margin: 0;
}

.load-more {
  margin-top: 0.65rem;
}

.term-pagination {
  margin-top: 0.65rem;
}

.empty-message {
  color: $dark-text-secondary;
  font-size: 0.84rem;
}
</style>
